import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  FormLabel,
  Heading,
  Input,
  SimpleGrid,
  Text,
  useToast,
} from '@chakra-ui/react';
import { AppCard, AppSelect, OutlineButton, TableLoader, TablePagination } from '../../../../components/ui';
import {
  ACTIVITY_ACTION_TYPES,
  ACTIVITY_MODULES,
} from '../../../../data/staff/activityLogs';
import { usePagination } from '../../../../hooks/usePagination';
import { listActivityLogs } from '../../../../repositories/activityLog.repository';
import { listStaff } from '../../../../repositories/staff.repository';
import { StaffActivityTable } from '../StaffActivityTable';

export default function StaffActivitiesPage() {
  const toast = useToast();
  const [logs, setLogs] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [staffFilter, setStaffFilter] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [logsResult, staffResult] = await Promise.all([
        listActivityLogs({ page: 1, pageSize: 200 }),
        listStaff({ page: 1, pageSize: 200 }),
      ]);
      setLogs(logsResult.items || []);
      setStaffList(staffResult.items || []);
    } catch (err) {
      toast({
        title: err?.message || 'Failed to load activity logs',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const staffOptions = useMemo(
    () =>
      staffList.map((s) => ({
        value: s.staffId,
        label: s.fullName,
        description: s.staffId,
      })),
    [staffList],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return logs.filter((log) => {
      const matchQ =
        !q ||
        (log.summary || '').toLowerCase().includes(q) ||
        (log.staffName || '').toLowerCase().includes(q) ||
        String(log.staffId || '').toLowerCase().includes(q) ||
        String(log.entityId || '').toLowerCase().includes(q);
      const matchStaff = !staffFilter || log.staffId === staffFilter;
      const matchModule = !moduleFilter || log.module === moduleFilter;
      const matchAction = !actionFilter || log.actionType === actionFilter;
      return matchQ && matchStaff && matchModule && matchAction;
    });
  }, [logs, search, staffFilter, moduleFilter, actionFilter]);

  const {
    pageItems,
    page,
    pageSize,
    totalCount,
    totalPages,
    setPage,
    setPageSize,
  } = usePagination(filtered);

  const clearFilters = () => {
    setSearch('');
    setStaffFilter('');
    setModuleFilter('');
    setActionFilter('');
  };

  return (
    <Box>
      <Flex
        justify="space-between"
        align={{ base: 'start', md: 'center' }}
        gap={4}
        mb={6}
        wrap="wrap"
      >
        <Box>
          <Heading size={{ base: 'lg', xl: 'xl' }}>System Logs</Heading>
          <Text color="text.secondary" mt={2} maxW="760px" fontSize={{ base: '14px', xl: '15px' }}>
            Organisation-wide audit log — what staff users are doing across modules.
            Visible only to users with Staff management rights.
          </Text>
        </Box>
        <OutlineButton size="sm" onClick={loadData} isLoading={loading}>
          Refresh
        </OutlineButton>
      </Flex>

      <AppCard mb={4}>
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 5 }} spacing={3} alignItems="end">
          <Box>
            <FormLabel fontSize="13px" mb={2}>
              Search
            </FormLabel>
            <Input
              h="40px"
              fontSize="14px"
              placeholder="Summary / staff / reference"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Box>
          <AppSelect
            label="Staff"
            labelFontSize="13px"
            h="40px"
            placeholder="All staff"
            value={staffFilter}
            onChange={(v) => setStaffFilter(v ?? '')}
            options={[{ value: '', label: 'All staff' }, ...staffOptions]}
            isSearchable
          />
          <AppSelect
            label="Module"
            labelFontSize="13px"
            h="40px"
            placeholder="All modules"
            value={moduleFilter}
            onChange={(v) => setModuleFilter(v ?? '')}
            options={[
              { value: '', label: 'All modules' },
              ...ACTIVITY_MODULES.map((m) => ({ value: m, label: m })),
            ]}
          />
          <AppSelect
            label="Action type"
            labelFontSize="13px"
            h="40px"
            placeholder="All types"
            value={actionFilter}
            onChange={(v) => setActionFilter(v ?? '')}
            options={[
              { value: '', label: 'All types' },
              ...ACTIVITY_ACTION_TYPES,
            ]}
          />
          <OutlineButton size="sm" w="100%" h="40px" minH="40px" onClick={clearFilters}>
            Clear filters
          </OutlineButton>
        </SimpleGrid>
      </AppCard>

      <AppCard p={0} overflow="hidden">
        {loading ? (
          <TableLoader rows={7} columns={5} />
        ) : (
          <>
            <Box px={{ base: 5, xl: 6 }} pt={{ base: 5, xl: 6 }} pb={3}>
              <Text fontSize={{ base: '13px', xl: '14px' }} color="text.secondary">
                {`${totalCount} log${totalCount === 1 ? '' : 's'}`}
              </Text>
            </Box>
            <StaffActivityTable
              rows={pageItems}
              showStaffColumn
              emptyMessage="No activity logs match your filters."
            />
            <TablePagination
              page={page}
              pageSize={pageSize}
              totalCount={totalCount}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </>
        )}
      </AppCard>
    </Box>
  );
}
